// Analytics page: everything beyond the visitor funnel, for the chosen period
// and component.
//  - LeadInsights (every plan): what kind of lead each component produced
//    (estimate, callback, instant quote, contact form), results by component
//    type, contact form topics and the homes asking (property type, heating,
//    heat pump size, price). Built from GET /v1/addresses (homes + enquiries)
//    and the stats per component.
//  - QuoteInsights (QuoteFlow only): the quote funnel, conversion, value, speed
//    (time to open, time to answer), where quotes came from, which options win,
//    why quotes are declined, cash vs finance and quotes over time. Built from
//    the quotes list (100-api quotes GET /v1/quotes).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, type EnquiryRecord, type HomeRecord, type Stats, type StatsRange, type Widget } from "../api";
import { useQuotesApi, type QuoteAccess, type QuoteSummary } from "../quotes/api";
import { gbp } from "../quotes/format";
import { CountUp, fmt, pct } from "./analytics";
import { productParam, type ProductFilter } from "./ProductSwitch";

const DAY = 24 * 3600 * 1000;
const RANGE_DAYS: Record<StatsRange, number> = { "7d": 7, "30d": 30, "90d": 90, "180d": 180, "1y": 365 };
const VAT_KEY = "xq-dash-value-inc-vat"; // shared with the dashboard's Value accepted card

/** True when an ISO time falls inside the last `days` days. */
const inRange = (iso: string | null | undefined, days: number) => !!iso && Date.now() - new Date(iso).getTime() <= days * DAY;

/** The middle value (null for none): robust to one quote left open for weeks. */
function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** A duration in plain words: "40 min", "5 h", "2.5 days". */
function duration(ms: number | null): string {
  if (ms === null) return "—";
  const min = ms / 60000;
  if (min < 90) return `${Math.max(1, Math.round(min))} min`;
  const h = min / 60;
  if (h < 36) return `${Math.round(h)} h`;
  return `${(Math.round((h / 24) * 10) / 10).toLocaleString("en-GB")} days`;
}

/** Plain names for each component type (the widget's layout style). */
const TYPE_LABELS: Record<string, string> = {
  small: "ASHP Estimator – Compact",
  full: "ASHP Estimator – Full",
  hosted: "Single page website",
  contact: "Contact form",
  "solar-full": "Solar estimate – Full",
  "solar-hosted": "Solar estimate – Single page website",
};
/** A component's row in "By component type": its style, kept apart for solar estimates. */
const styleOf = (w: Widget | undefined) => {
  const style = w?.branding?.layout?.style ?? "small";
  return w?.product === "solar" ? (style === "hosted" ? "solar-hosted" : "solar-full") : style;
};

/**
 * One horizontal bar per row, as a share of the largest (the Funnel's look,
 * reused for breakdowns). Rows with a zero count still show their label.
 */
function Bars({ rows, total, empty }: { rows: { label: string; value: number; note?: string }[]; total?: number; empty: string }) {
  const max = Math.max(...rows.map((r) => r.value), 0);
  const of = total ?? rows.reduce((n, r) => n + r.value, 0);
  if (max === 0) return <p className="muted">{empty}</p>;
  return (
    <ol className="funnel insight-bars">
      {rows.map((r, i) => (
        <li key={r.label} className="funnel-row" style={{ ["--i" as string]: i }}>
          <span className="funnel-label">{r.label}</span>
          <span className="funnel-track" aria-hidden="true">
            <span className="funnel-bar" style={{ width: `${Math.max((r.value / max) * 100, r.value > 0 ? 0.5 : 0)}%` }} />
          </span>
          <span className="funnel-value">
            <strong>{fmt(r.value)}</strong> <span className="muted">{pct(r.value, of)}</span>
          </span>
          <span className="funnel-drop">{r.note ?? ""}</span>
        </li>
      ))}
    </ol>
  );
}

/** Count items by a key (missing keys go under `other`), largest first. */
function countBy<T>(items: T[], key: (x: T) => string | null | undefined, other = "Not given"): { label: string; value: number }[] {
  const m = new Map<string, number>();
  for (const x of items) {
    const k = key(x) || other;
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return [...m.entries()].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/[_-]/g, " ");

// ─── Leads ────────────────────────────────────────────────────────────────────

export function LeadInsights({
  range,
  widgetId,
  stats,
  widgets,
  period,
  product = "all",
}: {
  range: StatsRange;
  widgetId: string;
  stats: Stats | null;
  widgets: Widget[];
  period: string;
  product?: ProductFilter;
}) {
  const api = useApi();
  const [homes, setHomes] = useState<HomeRecord[] | null>(null);
  const [enquiries, setEnquiries] = useState<EnquiryRecord[]>([]);

  // Homes and contact form enquiries for the period (reloads with the filters)
  useEffect(() => {
    let live = true;
    setHomes(null);
    api
      .getAddresses(range, widgetId || undefined, productParam(product))
      .then((r) => {
        if (!live) return;
        setHomes(r.addresses);
        setEnquiries(r.enquiries ?? []);
      })
      .catch(() => live && setHomes([]));
    return () => {
      live = false;
    };
  }, [api, range, widgetId, product]);

  const byId = useMemo(() => new Map(widgets.map((w) => [w.widgetId, w])), [widgets]);

  const figures = useMemo(() => {
    const h = homes ?? [];
    const priced = h.filter((x) => x.quote && !x.quote.noEstimate && !x.quote.tooBig);
    const instant = h.filter((x) => x.callbackRequested && x.callbackDelivery?.method === "quote");
    const callbacks = h.filter((x) => x.callbackRequested && x.callbackDelivery?.method !== "quote");
    const kws = priced.map((x) => x.quote!.heatPumpKw).filter((n) => n > 0);
    const prices = priced.map((x) => x.quote!.price ?? (x.quote!.low + x.quote!.high) / 2).filter((n) => n > 0);
    // Solar callbacks (kind "solar") and contact form enquiries come back together
    const solarLeads = enquiries.filter((e) => e.kind === "solar");
    const contacts = enquiries.filter((e) => e.kind !== "solar");
    const systems = solarLeads.flatMap((e) => (e.system ? [e.system] : []));
    return {
      solarLeads,
      contacts,
      medianPanels: median(systems.map((x) => x.panels)),
      medianKwp: median(systems.map((x) => x.kwp)),
      medianSolarPrice: median(systems.map((x) => (x.low + x.high) / 2)),
      priced,
      instant,
      callbacks,
      surveyOnly: h.filter((x) => x.quote?.noEstimate || x.quote?.tooBig).length,
      medianKw: median(kws),
      medianPrice: median(prices),
      grants: priced.filter((x) => (x.quote!.grant ?? 0) > 0).length,
      types: countBy(h, (x) => (x.details?.propertyType ? cap(x.details.propertyType) : null)),
      heating: countBy(h, (x) => (x.details?.heating ? cap(x.details.heating) : null)),
      beds: countBy(h, (x) => (x.details?.bedrooms ? `${x.details.bedrooms} bedroom${x.details.bedrooms === 1 ? "" : "s"}` : null)),
      // Heat pump sizes in 2 kW bands (the question installers ask about stock)
      sizes: countBy(priced, (x) => {
        const kw = x.quote!.heatPumpKw;
        const lo = Math.floor(kw / 2) * 2;
        return kw > 0 ? `${lo}–${lo + 2} kW` : null;
      }).sort((a, b) => parseFloat(a.label) - parseFloat(b.label)),
      topics: countBy(contacts, (e) => e.topic, "No topic chosen"),
    };
  }, [homes, enquiries]);

  // Results by component type: the stats per component, grouped by layout style
  const types = useMemo(() => {
    const rows = new Map<string, { loads: number; prices: number; leads: number; components: number }>();
    for (const r of stats?.widgets ?? []) {
      const t = styleOf(byId.get(r.widgetId));
      const row = rows.get(t) ?? { loads: 0, prices: 0, leads: 0, components: 0 };
      rows.set(t, { loads: row.loads + r.loaded, prices: row.prices + r.quote, leads: row.leads + r.lead, components: row.components + 1 });
    }
    // (Contact forms count their sent enquiries as "lead" too, so they fit the same table)
    return [...rows.entries()].sort((a, b) => b[1].loads - a[1].loads);
  }, [stats, byId]);

  const solar = product === "solar";
  const leadTotal = figures.callbacks.length + figures.instant.length + enquiries.length;
  // Each product's rows only where it can have any (the switch already filtered the data)
  const mix = [
    ...(solar ? [] : [{ label: "Heat pump callback requests", value: figures.callbacks.length, note: "Estimate, then details" }]),
    ...(solar ? [] : [{ label: "Instant detailed quotes", value: figures.instant.length, note: "QuoteFlow" }]),
    ...(product === "ashp" ? [] : [{ label: "Solar callback requests", value: figures.solarLeads.length, note: "Roof scan, then details" }]),
    ...(product === "all" ? [{ label: "Contact form enquiries", value: figures.contacts.length, note: "Contact form" }] : []),
  ];
  // Solar's headline figures: roofs scanned and the systems people asked about
  const solarKpis = [
    { label: "Leads", value: homes ? leadTotal : null, note: period },
    { label: "Roofs scanned", value: stats ? (stats.totals.scan ?? 0) : null, note: period },
    {
      label: "Typical system",
      text: figures.medianPanels === null ? "—" : `${figures.medianPanels} panels`,
      note: figures.medianKwp === null ? "Middle of the systems asked about" : `About ${figures.medianKwp.toFixed(1)} kWp`,
    },
    { label: "Typical estimate", text: figures.medianSolarPrice === null ? "—" : gbp(Math.round(figures.medianSolarPrice)), note: "Middle of the ranges shown" },
  ];

  return (
    <div className="insight-leads">
      <section className="kpi-row" aria-label="Leads">
        {(solar ? solarKpis : [
          { label: "Leads", value: homes ? leadTotal : null, note: period },
          { label: "Estimates shown", value: homes ? figures.priced.length : null, note: homes ? `${fmt(figures.surveyOnly)} needed a survey first` : period },
          { label: "Typical heat pump", text: figures.medianKw === null ? "—" : `${figures.medianKw.toLocaleString("en-GB")} kW`, note: "Middle of the estimates shown" },
          { label: "Typical estimate", text: figures.medianPrice === null ? "—" : gbp(Math.round(figures.medianPrice)), note: homes ? `${fmt(figures.grants)} eligible for the grant` : period },
        ] as { label: string; value?: number | null; text?: string; note: string }[]).map((k, i) => (
          <div key={k.label} className="card kpi" style={{ ["--i" as string]: i }}>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value">{k.text !== undefined ? k.text : k.value == null ? "—" : <CountUp value={k.value} delay={i * 90} />}</div>
            <div className="kpi-note">{k.note}</div>
          </div>
        ))}
      </section>

      <div className="insight-grid">
        <section className="card">
          <h2>Lead types</h2>
          <p className="card-sub">What each lead asked for, {period.toLowerCase()}. The same badges as on Leads.</p>
          {homes === null ? <p className="muted">Loading…</p> : <Bars rows={mix} empty="No leads in this period yet." />}
          {homes && leadTotal > 0 && (
            <p className="insight-foot">
              <Link to="/leads" className="link-button small">
                Open Leads
              </Link>
            </p>
          )}
        </section>
        {/* Contact forms are only in "All" */}
        {product === "all" && (
        <section className="card">
          <h2>Contact form topics</h2>
          <p className="card-sub">What people chose in the enquiry drop-down.</p>
          <Bars rows={figures.topics} empty="No contact form enquiries in this period yet." />
        </section>
        )}
      </div>

      {!widgetId && (
        <section className="card">
          <h2>By component type</h2>
          {types.length === 0 ? (
            <p className="muted">No activity in this period yet.</p>
          ) : (
            <div className="table-scroll">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th scope="col">Type</th>
                    <th scope="col">Components</th>
                    <th scope="col">Loads</th>
                    <th scope="col">Prices shown</th>
                    <th scope="col">Leads</th>
                    <th scope="col">Load → lead</th>
                  </tr>
                </thead>
                <tbody>
                  {types.map(([t, r]) => (
                    <tr key={t}>
                      <th scope="row">{TYPE_LABELS[t] ?? cap(t)}</th>
                      <td>{fmt(r.components)}</td>
                      <td>{fmt(r.loads)}</td>
                      <td>{t === "contact" ? "—" : fmt(r.prices)}</td>
                      <td>{fmt(r.leads)}</td>
                      <td>{pct(r.leads, r.loads) ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* The homes come from heat pump estimates (EPC answers): not shown for solar */}
      {!solar && (
      <section className="card">
        <h2>The homes asking</h2>
        <p className="card-sub">Every home looked up in the period, from its EPC. Addresses only: never who lives there.</p>
        {homes === null ? (
          <p className="muted">Loading…</p>
        ) : homes.length === 0 ? (
          <p className="muted">No homes looked up in this period yet.</p>
        ) : (
          <div className="insight-homes">
            <div>
              <h3>Property type</h3>
              <Bars rows={figures.types.slice(0, 6)} total={homes.length} empty="—" />
            </div>
            <div>
              <h3>Heating today</h3>
              <Bars rows={figures.heating.slice(0, 6)} total={homes.length} empty="—" />
            </div>
            <div>
              <h3>Bedrooms</h3>
              <Bars rows={figures.beds.slice(0, 6)} total={homes.length} empty="—" />
            </div>
            <div>
              <h3>Heat pump size</h3>
              <Bars rows={figures.sizes} total={figures.priced.length} empty="No estimates with a size yet." />
            </div>
          </div>
        )}
      </section>
      )}
    </div>
  );
}

// ─── QuoteFlow ────────────────────────────────────────────────────────────────

type QuoteRow = QuoteSummary & {
  acceptance?: { totals: { net: number; vat: number; gross: number; payable: number }; optionName: string; signedAt?: string; payment?: "cash" | "finance"; offline?: boolean };
  decline?: { at?: string; reason?: string; offline?: boolean } | null;
  source?: { widgetId?: string; channel?: string } | null;
};

/** Where a quote came from: an instant widget quote, a lead turned into a quote, or built from scratch. */
const sourceOf = (q: QuoteRow) => (q.source?.channel === "widget" ? "Instant quote (website)" : q.source?.widgetId ? "From a lead" : "Built in the console");
const acceptedAt = (q: QuoteRow) => q.acceptance?.signedAt ?? q.updatedAt;
const declinedAt = (q: QuoteRow) => q.decline?.at ?? q.updatedAt;
const sentAt = (q: QuoteRow) => q.publish?.firstSentAt ?? q.publish?.sentAt;

export function QuoteInsights({ range, widgetId, period }: { range: StatsRange; widgetId: string; period: string }) {
  const api = useQuotesApi();
  const [access, setAccess] = useState<QuoteAccess | null>(null);
  const [quotes, setQuotes] = useState<QuoteRow[] | null>(null);
  const [measure, setMeasure] = useState<"sent" | "accepted" | "value">("sent");
  const [hover, setHover] = useState<number | null>(null);
  const [incVat, setIncVat] = useState<boolean>(() => {
    try {
      return localStorage.getItem(VAT_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const toggleVat = (v: boolean) => {
    setIncVat(v);
    try {
      localStorage.setItem(VAT_KEY, v ? "1" : "0");
    } catch {
      // the choice just won't be remembered
    }
  };

  // QuoteFlow companies only (a failed check shows nothing)
  useEffect(() => {
    api
      .getAccess()
      .then((a) => {
        setAccess(a);
        if (a.entitled)
          api
            .listQuotes()
            .then((q) => setQuotes(q as QuoteRow[]))
            .catch(() => setQuotes([]));
      })
      .catch(() => undefined);
  }, [api]);

  const days = RANGE_DAYS[range];

  const f = useMemo(() => {
    // One component chosen: only the quotes that came from it
    const all = (quotes ?? []).filter((q) => !widgetId || q.source?.widgetId === widgetId);
    const created = all.filter((q) => inRange(q.createdAt, days));
    const sent = all.filter((q) => inRange(sentAt(q), days));
    const opened = sent.filter((q) => (q.publish?.views ?? 0) > 0);
    const accepted = all.filter((q) => q.status === "accepted" && inRange(acceptedAt(q), days));
    const declined = all.filter((q) => q.status === "declined" && inRange(declinedAt(q), days));
    const value = (q: QuoteRow) => (incVat ? q.acceptance?.totals.gross : q.acceptance?.totals.net) ?? 0;
    const won = accepted.reduce((n, q) => n + value(q), 0);
    // Speed: from first sent to first opened, and to the answer
    const toOpen = sent.filter((q) => q.publish?.viewedAt).map((q) => new Date(q.publish!.viewedAt!).getTime() - new Date(sentAt(q)!).getTime());
    const toAnswer = [...accepted.map((q) => [q, acceptedAt(q)] as const), ...declined.map((q) => [q, declinedAt(q)] as const)]
      .filter(([q]) => sentAt(q))
      .map(([q, at]) => new Date(at!).getTime() - new Date(sentAt(q)!).getTime());
    // Sources: how many each produced, sent, won and for how much
    const sources = new Map<string, { quotes: number; sent: number; accepted: number; value: number }>();
    for (const q of created) {
      const s = sources.get(sourceOf(q)) ?? { quotes: 0, sent: 0, accepted: 0, value: 0 };
      sources.set(sourceOf(q), { ...s, quotes: s.quotes + 1 });
    }
    for (const q of sent) {
      const s = sources.get(sourceOf(q)) ?? { quotes: 0, sent: 0, accepted: 0, value: 0 };
      sources.set(sourceOf(q), { ...s, sent: s.sent + 1 });
    }
    for (const q of accepted) {
      const s = sources.get(sourceOf(q)) ?? { quotes: 0, sent: 0, accepted: 0, value: 0 };
      sources.set(sourceOf(q), { ...s, accepted: s.accepted + 1, value: s.value + value(q) });
    }
    // Still open: sent, not answered, still inside the period
    const open = all.filter((q) => (q.status === "sent" || q.status === "viewed") && sentAt(q));
    const openValue = open.reduce((n, q) => n + (q.summary?.payable ?? 0), 0);
    return {
      created,
      sent,
      opened,
      accepted,
      declined,
      won,
      open,
      openValue,
      avg: accepted.length ? won / accepted.length : null,
      rate: accepted.length + declined.length ? accepted.length / (accepted.length + declined.length) : null,
      medianOpen: median(toOpen),
      medianAnswer: median(toAnswer),
      sources: [...sources.entries()].sort((a, b) => b[1].quotes - a[1].quotes),
      options: countBy(accepted, (q) => q.acceptance?.optionName, "Unnamed option"),
      reasons: countBy(declined, (q) => q.decline?.reason?.trim(), "No reason given"),
      payment: [
        { label: "Cash", value: accepted.filter((q) => q.acceptance?.payment !== "finance").length },
        { label: "Finance", value: accepted.filter((q) => q.acceptance?.payment === "finance").length },
      ],
      offline: accepted.filter((q) => q.acceptance?.offline).length,
      value,
    };
  }, [quotes, widgetId, days, incVat]);

  // Quotes over time: per day up to 30 days, per week beyond (like the visitor chart)
  const buckets = useMemo(() => {
    const size = days > 60 ? 7 : 1;
    const n = Math.ceil(days / size);
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const start = today.getTime() - (days - 1) * DAY;
    const out = Array.from({ length: n }, (_, i) => ({ start: new Date(start + i * size * DAY), end: new Date(Math.min(start + (i * size + size - 1) * DAY, today.getTime())), value: 0 }));
    const add = (iso: string | null | undefined, v: number) => {
      if (!iso) return;
      const i = Math.floor((new Date(iso).getTime() - start) / DAY / size);
      if (i >= 0 && i < n) out[i].value += v;
    };
    if (measure === "sent") f.sent.forEach((q) => add(sentAt(q), 1));
    if (measure === "accepted") f.accepted.forEach((q) => add(acceptedAt(q), 1));
    if (measure === "value") f.accepted.forEach((q) => add(acceptedAt(q), f.value(q)));
    return { list: out, weekly: size === 7 };
  }, [f, days, measure]);

  if (!access?.entitled) return null;

  const funnel = [
    { label: "Quotes created", value: f.created.length },
    { label: "Sent to the customer", value: f.sent.length },
    { label: "Opened by the customer", value: f.opened.length },
    { label: "Accepted", value: f.accepted.length },
  ];
  const max = Math.max(...buckets.list.map((b) => b.value), 0);
  const dayFmt = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  const tickEvery = Math.max(1, Math.ceil(buckets.list.length / 6));
  const show = (v: number) => (measure === "value" ? gbp(Math.round(v)) : fmt(v));
  const MEASURES = { sent: "Quotes sent", accepted: "Quotes accepted", value: "Value accepted" } as const;

  return (
    <section className="insight-quoteflow" aria-label="QuoteFlow analytics">
      <div className="dash-quoteflow-head">
        <h2>QuoteFlow</h2>
        <label className="kpi-switch" title="Show values including or excluding VAT">
          <input type="checkbox" role="switch" checked={incVat} onChange={(e) => toggleVat(e.target.checked)} aria-label="Include VAT" />
          <span className="kpi-switch-track" aria-hidden="true" />
          Values inc VAT
        </label>
      </div>

      <div className="kpi-row">
        {[
          { label: "Quotes sent", text: quotes ? fmt(f.sent.length) : "—", note: quotes ? `${pct(f.opened.length, f.sent.length) ?? "—"} opened` : period },
          { label: "Acceptance rate", text: f.rate === null ? "—" : `${Math.round(f.rate * 1000) / 10}%`, note: `${fmt(f.accepted.length)} accepted, ${fmt(f.declined.length)} declined` },
          { label: "Value accepted", text: quotes ? gbp(Math.round(f.won)) : "—", note: f.avg === null ? "Before grants" : `Average ${gbp(Math.round(f.avg))}, before grants` },
          { label: "Waiting for an answer", text: quotes ? fmt(f.open.length) : "—", note: f.open.length ? `${gbp(Math.round(f.openValue))} to pay if all accepted` : "Nothing open" },
        ].map((k, i) => (
          <div key={k.label} className="card kpi" style={{ ["--i" as string]: i }}>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value">{k.text}</div>
            <div className="kpi-note">{k.note}</div>
          </div>
        ))}
      </div>

      <div className="insight-grid">
        <section className="card">
          <h2>Quote journey</h2>
          <p className="card-sub">{period}. Each quote counts once per step.</p>
          <Bars rows={funnel} total={f.created.length || f.sent.length} empty="No quotes in this period yet." />
        </section>
        <section className="card">
          <h2>Speed</h2>
          <p className="card-sub">Typical times (the middle quote), from first sent.</p>
          <dl className="insight-speed">
            <div>
              <dt>To first opened</dt>
              <dd>{duration(f.medianOpen)}</dd>
            </div>
            <div>
              <dt>To an answer</dt>
              <dd>{duration(f.medianAnswer)}</dd>
            </div>
            <div>
              <dt>Accepted offline</dt>
              <dd>{fmt(f.offline)}</dd>
            </div>
          </dl>
        </section>
      </div>

      <section className="card trend-card">
        <div className="trend-head">
          <div>
            <h2>{MEASURES[measure]} over time</h2>
            <p className="card-sub">{buckets.weekly ? "Per week" : "Per day"}, UTC.</p>
          </div>
          <div className="ed-segmented" role="group" aria-label="Measure">
            {(Object.keys(MEASURES) as (keyof typeof MEASURES)[]).map((m) => (
              <button key={m} type="button" className={m === measure ? "is-on" : ""} aria-pressed={m === measure} onClick={() => setMeasure(m)}>
                {MEASURES[m]}
              </button>
            ))}
          </div>
        </div>
        {max === 0 ? (
          <p className="muted">Nothing in this period yet.</p>
        ) : (
          <div className="trend" role="img" aria-label={`${MEASURES[measure]} ${buckets.weekly ? "per week" : "per day"}, peak ${show(max)}`}>
            <div className="trend-plot">
              <span className="trend-max">{show(max)}</span>
              {buckets.list.map((b, i) => (
                <div
                  key={`${measure}-${b.start.toISOString()}`}
                  className={`trend-col${hover === i ? " is-hover" : ""}`}
                  style={{ ["--i" as string]: i, ["--n" as string]: buckets.list.length }}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                >
                  <span className="trend-bar" style={{ height: `${(b.value / max) * 100}%` }} />
                  {hover === i && (
                    <span className={`trend-tip${i > buckets.list.length / 2 ? " is-left" : ""}`} role="tooltip">
                      <strong>{buckets.weekly ? `${dayFmt(b.start)} – ${dayFmt(b.end)}` : dayFmt(b.start)}</strong>
                      {show(b.value)} {measure === "value" ? "accepted" : MEASURES[measure].toLowerCase()}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="trend-axis" aria-hidden="true">
              {buckets.list.map((b, i) => (
                <span key={b.start.toISOString()}>{i % tickEvery === 0 || i === buckets.list.length - 1 ? dayFmt(b.start) : ""}</span>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Where quotes come from</h2>
        {f.sources.length === 0 ? (
          <p className="muted">No quotes in this period yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="stats-table">
              <thead>
                <tr>
                  <th scope="col">Source</th>
                  <th scope="col">Created</th>
                  <th scope="col">Sent</th>
                  <th scope="col">Accepted</th>
                  <th scope="col">Sent → accepted</th>
                  <th scope="col">Value accepted</th>
                </tr>
              </thead>
              <tbody>
                {f.sources.map(([s, r]) => (
                  <tr key={s}>
                    <th scope="row">{s}</th>
                    <td>{fmt(r.quotes)}</td>
                    <td>{fmt(r.sent)}</td>
                    <td>{fmt(r.accepted)}</td>
                    <td>{pct(r.accepted, r.sent) ?? "—"}</td>
                    <td>{gbp(Math.round(r.value))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="insight-grid insight-grid-3">
        <section className="card">
          <h2>Options chosen</h2>
          <p className="card-sub">Which option customers accepted.</p>
          <Bars rows={f.options} empty="No accepted quotes in this period yet." />
        </section>
        <section className="card">
          <h2>How they pay</h2>
          <p className="card-sub">Accepted quotes, cash or finance.</p>
          <Bars rows={f.payment} empty="No accepted quotes in this period yet." />
        </section>
        <section className="card">
          <h2>Why quotes are declined</h2>
          <p className="card-sub">The customer's own words.</p>
          <Bars rows={f.reasons.slice(0, 6)} empty="No declined quotes in this period." />
        </section>
      </div>
    </section>
  );
}
